/* Copy da área Usuários (users/): lista + picker + detalhe (overview, orgs, devices, licenses,
   sessions, auditoria). Modais + catálogo de erros moram em `users-modals.ts` só por causa do
   limite de 500 LOC (§4.7); `useUsersContent()` funde os dois módulos para quem consome. */
import type { Bilingual } from '../bilingual.js';
import { useBilingual } from '../locale-provider.js';
import {
  USERS_MODALS_CONTENT,
  type UserErrorKey,
  type UsersModalsContent,
} from './users-modals.js';

export type { UserErrorKey };

interface UsersListContent {
  readonly common: {
    readonly noResultsTitle: string;
    readonly clearFilters: string;
    readonly countOf: (shown: number, total: number) => string;
  };
  readonly table: {
    readonly columnEmail: string;
    readonly columnName: string;
    readonly columnStatus: string;
    readonly columnCreatedAt: string;
    readonly statusActive: string;
    readonly statusBlocked: string;
    readonly emptyTitle: string;
    readonly emptyDescription: string;
    readonly emptyFilteredDescription: string;
  };
  readonly filters: {
    readonly searchLabel: string;
    readonly searchPlaceholder: string;
    readonly verifiedFilterLabel: string;
    readonly optionAll: string;
    readonly optionVerified: string;
    readonly optionUnverified: string;
  };
  readonly picker: {
    readonly defaultPlaceholder: string;
    readonly searchPlaceholder: string;
    readonly searchAriaLabel: string;
  };
  readonly detailHeader: {
    readonly avatarAlt: (email: string) => string;
    readonly impersonate: string;
    readonly block: string;
    readonly unblock: string;
    readonly accountActive: string;
    readonly accountBlocked: string;
    readonly blockedAt: (date: string) => string;
    readonly blockedReason: (reason: string) => string;
    readonly unavailableMetric: string;
    readonly emailVerified: string;
    readonly emailUnverified: string;
    readonly summaryOrgs: string;
    readonly summaryDevices: string;
    readonly summaryLicenses: string;
    readonly summaryActiveSessions: string;
    readonly summaryLastActivity: string;
    readonly sessionsFraction: (active: number, total: number) => string;
    readonly licensesHint: (active: number, expired: number, perpetual: number) => string;
  };
  readonly overviewTab: {
    readonly name: string;
    readonly username: string;
    readonly emailVerifiedAt: string;
    readonly emailUnverified: string;
    readonly createdAt: string;
    readonly updatedAt: string;
    readonly orgsCount: string;
    readonly activeLicensesCount: string;
    readonly idLabel: string;
  };
  readonly orgsTab: {
    readonly empty: string;
    readonly searchAriaLabel: string;
    readonly searchPlaceholder: string;
    readonly emptyFilteredDescription: string;
    readonly columnOrg: string;
    readonly columnRole: string;
    readonly columnJoinedAt: string;
  };
  readonly devicesTab: {
    readonly empty: string;
    readonly filterAriaLabel: string;
    readonly optionAll: string;
    readonly optionActive: string;
    readonly optionSuspect: string;
    readonly optionCooldown: string;
    readonly emptyFilteredDescription: string;
    readonly columnDevice: string;
    readonly columnVersion: string;
    readonly columnLastSeen: string;
    readonly columnStatus: string;
    readonly statusCooldown: string;
    readonly statusSuspect: string;
    readonly statusActive: string;
    readonly cooldownAction: string;
  };
  readonly licensesTab: {
    readonly issueButton: string;
    readonly empty: string;
    readonly filterAriaLabel: string;
    readonly optionAll: string;
    readonly optionActive: string;
    readonly optionExpired: string;
    readonly optionPerpetual: string;
    readonly typeFacetLabel: string;
    readonly emptyFilteredDescription: string;
    readonly columnPack: string;
    readonly columnType: string;
    readonly columnVersion: string;
    readonly columnAcquired: string;
    readonly columnExpires: string;
    readonly perpetualValue: string;
    readonly actionsAriaLabel: string;
    readonly revokeAction: string;
    readonly transferAction: string;
  };
  readonly sessionsTab: {
    readonly empty: string;
    readonly filterAriaLabel: string;
    readonly optionAll: string;
    readonly optionActive: string;
    readonly optionRevoked: string;
    readonly emptyFilteredDescription: string;
    readonly columnDevice: string;
    readonly columnOrg: string;
    readonly columnCreatedAt: string;
    readonly columnLastUsed: string;
    readonly columnExpires: string;
    readonly columnStatus: string;
    readonly statusActive: string;
    readonly statusRevoked: string;
    readonly revokeAction: string;
  };
  readonly auditTab: {
    readonly scopeDescription: string;
    readonly viewFullAudit: string;
    readonly actionLabel: string;
    readonly emptyTitle: string;
    readonly emptyFilteredDescription: string;
  };
}

type UsersContent = UsersListContent & UsersModalsContent;

const USERS_LIST_CONTENT: Bilingual<UsersListContent> = {
  'pt-BR': {
    common: {
      noResultsTitle: 'Nenhum resultado',
      clearFilters: 'Limpar filtros',
      countOf: (shown, total) => `${shown} de ${total}`,
    },
    table: {
      columnEmail: 'Email',
      columnName: 'Nome',
      columnStatus: 'Status',
      columnCreatedAt: 'Criado em',
      statusActive: 'Ativo',
      statusBlocked: 'Bloqueado',
      emptyTitle: 'Nenhum usuário',
      emptyDescription: 'Busque por email (mín. 2 caracteres) para encontrar usuários.',
      emptyFilteredDescription: 'Nenhum usuário corresponde à busca ou aos filtros.',
    },
    filters: {
      searchLabel: 'Buscar usuários',
      searchPlaceholder: 'Buscar por email…',
      verifiedFilterLabel: 'Filtrar por verificação de e-mail',
      optionAll: 'Todos',
      optionVerified: 'Verificados',
      optionUnverified: 'Não verificados',
    },
    picker: {
      defaultPlaceholder: 'Selecionar usuário de destino...',
      searchPlaceholder: 'Buscar por email...',
      searchAriaLabel: 'Buscar destino',
    },
    detailHeader: {
      avatarAlt: (email) => `Avatar de ${email}`,
      impersonate: 'Impersonar',
      block: 'Bloquear usuário',
      unblock: 'Desbloquear usuário',
      accountActive: 'Conta ativa',
      accountBlocked: 'Conta bloqueada',
      blockedAt: (date) => `Bloqueada em ${date}`,
      blockedReason: (reason) => `Motivo: ${reason}`,
      unavailableMetric: 'Indisponível',
      emailVerified: 'Email verificado',
      emailUnverified: 'Não verificado',
      summaryOrgs: 'Organizações',
      summaryDevices: 'Devices',
      summaryLicenses: 'Licenses',
      summaryActiveSessions: 'Sessões ativas',
      summaryLastActivity: 'Última atividade',
      sessionsFraction: (active, total) => `${active}/${total}`,
      licensesHint: (active, expired, perpetual) =>
        `${active} ativas · ${expired} expiradas · ${perpetual} perpétuas`,
    },
    overviewTab: {
      name: 'Nome',
      username: 'Username',
      emailVerifiedAt: 'Email verificado em',
      emailUnverified: 'Não verificado',
      createdAt: 'Criado em',
      updatedAt: 'Atualizado em',
      orgsCount: 'Organizações',
      activeLicensesCount: 'Licenses ativas',
      idLabel: 'ID',
    },
    orgsTab: {
      empty: 'Sem organizações',
      searchAriaLabel: 'Buscar organizações',
      searchPlaceholder: 'Buscar por nome…',
      emptyFilteredDescription: 'Nenhuma organização corresponde à busca.',
      columnOrg: 'Organização',
      columnRole: 'Papel',
      columnJoinedAt: 'Entrou em',
    },
    devicesTab: {
      empty: 'Sem devices',
      filterAriaLabel: 'Filtrar devices por status',
      optionAll: 'Todos',
      optionActive: 'Ativos',
      optionSuspect: 'Suspeitos',
      optionCooldown: 'Em cooldown',
      emptyFilteredDescription: 'Nenhum device corresponde ao filtro.',
      columnDevice: 'Device',
      columnVersion: 'Versão',
      columnLastSeen: 'Visto em',
      columnStatus: 'Status',
      statusCooldown: 'Cooldown',
      statusSuspect: 'Suspeito',
      statusActive: 'Ativo',
      cooldownAction: 'Cooldown',
    },
    licensesTab: {
      issueButton: 'Emitir license manual',
      empty: 'Sem licenses ativas',
      filterAriaLabel: 'Filtrar licenses por vigência',
      optionAll: 'Todas',
      optionActive: 'Ativas',
      optionExpired: 'Expiradas',
      optionPerpetual: 'Perpétuas',
      typeFacetLabel: 'Tipo',
      emptyFilteredDescription: 'Nenhuma license corresponde aos filtros.',
      columnPack: 'Pack',
      columnType: 'Tipo',
      columnVersion: 'Versão',
      columnAcquired: 'Adquirida',
      columnExpires: 'Expira',
      perpetualValue: 'Perpétua',
      actionsAriaLabel: 'Ações da license',
      revokeAction: 'Revogar',
      transferAction: 'Transferir',
    },
    sessionsTab: {
      empty: 'Sem sessões',
      filterAriaLabel: 'Filtrar sessões por status',
      optionAll: 'Todas',
      optionActive: 'Ativas',
      optionRevoked: 'Revogadas',
      emptyFilteredDescription: 'Nenhuma sessão corresponde ao filtro.',
      columnDevice: 'Device',
      columnOrg: 'Org',
      columnCreatedAt: 'Criada em',
      columnLastUsed: 'Último uso',
      columnExpires: 'Expira',
      columnStatus: 'Status',
      statusActive: 'Ativa',
      statusRevoked: 'Revogada',
      revokeAction: 'Revogar',
    },
    auditTab: {
      scopeDescription:
        'Auditoria administrativa: exibe ações de operadores sobre esta conta. Logins e atividade de sessão não fazem parte desta trilha.',
      viewFullAudit: 'Ver auditoria completa',
      actionLabel: 'Ação',
      emptyTitle: 'Sem ações registradas',
      emptyFilteredDescription: 'Nenhuma ação corresponde ao filtro.',
    },
  },
  'en-US': {
    common: {
      noResultsTitle: 'No results',
      clearFilters: 'Clear filters',
      countOf: (shown, total) => `${shown} of ${total}`,
    },
    table: {
      columnEmail: 'Email',
      columnName: 'Name',
      columnStatus: 'Status',
      columnCreatedAt: 'Created at',
      statusActive: 'Active',
      statusBlocked: 'Blocked',
      emptyTitle: 'No users',
      emptyDescription: 'Search by email (min. 2 characters) to find users.',
      emptyFilteredDescription: 'No user matches the search or filters.',
    },
    filters: {
      searchLabel: 'Search users',
      searchPlaceholder: 'Search by email…',
      verifiedFilterLabel: 'Filter by email verification',
      optionAll: 'All',
      optionVerified: 'Verified',
      optionUnverified: 'Unverified',
    },
    picker: {
      defaultPlaceholder: 'Select destination user...',
      searchPlaceholder: 'Search by email...',
      searchAriaLabel: 'Search destination',
    },
    detailHeader: {
      avatarAlt: (email) => `Avatar for ${email}`,
      impersonate: 'Impersonate',
      block: 'Block user',
      unblock: 'Unblock user',
      accountActive: 'Active account',
      accountBlocked: 'Blocked account',
      blockedAt: (date) => `Blocked at ${date}`,
      blockedReason: (reason) => `Reason: ${reason}`,
      unavailableMetric: 'Unavailable',
      emailVerified: 'Email verified',
      emailUnverified: 'Unverified',
      summaryOrgs: 'Organizations',
      summaryDevices: 'Devices',
      summaryLicenses: 'Licenses',
      summaryActiveSessions: 'Active sessions',
      summaryLastActivity: 'Last activity',
      sessionsFraction: (active, total) => `${active}/${total}`,
      licensesHint: (active, expired, perpetual) =>
        `${active} active · ${expired} expired · ${perpetual} perpetual`,
    },
    overviewTab: {
      name: 'Name',
      username: 'Username',
      emailVerifiedAt: 'Email verified at',
      emailUnverified: 'Unverified',
      createdAt: 'Created at',
      updatedAt: 'Updated at',
      orgsCount: 'Organizations',
      activeLicensesCount: 'Active licenses',
      idLabel: 'ID',
    },
    orgsTab: {
      empty: 'No organizations',
      searchAriaLabel: 'Search organizations',
      searchPlaceholder: 'Search by name…',
      emptyFilteredDescription: 'No organization matches the search.',
      columnOrg: 'Organization',
      columnRole: 'Role',
      columnJoinedAt: 'Joined at',
    },
    devicesTab: {
      empty: 'No devices',
      filterAriaLabel: 'Filter devices by status',
      optionAll: 'All',
      optionActive: 'Active',
      optionSuspect: 'Suspect',
      optionCooldown: 'In cooldown',
      emptyFilteredDescription: 'No device matches the filter.',
      columnDevice: 'Device',
      columnVersion: 'Version',
      columnLastSeen: 'Last seen',
      columnStatus: 'Status',
      statusCooldown: 'Cooldown',
      statusSuspect: 'Suspect',
      statusActive: 'Active',
      cooldownAction: 'Cooldown',
    },
    licensesTab: {
      issueButton: 'Issue manual license',
      empty: 'No active licenses',
      filterAriaLabel: 'Filter licenses by status',
      optionAll: 'All',
      optionActive: 'Active',
      optionExpired: 'Expired',
      optionPerpetual: 'Perpetual',
      typeFacetLabel: 'Type',
      emptyFilteredDescription: 'No license matches the filters.',
      columnPack: 'Pack',
      columnType: 'Type',
      columnVersion: 'Version',
      columnAcquired: 'Acquired',
      columnExpires: 'Expires',
      perpetualValue: 'Perpetual',
      actionsAriaLabel: 'License actions',
      revokeAction: 'Revoke',
      transferAction: 'Transfer',
    },
    sessionsTab: {
      empty: 'No sessions',
      filterAriaLabel: 'Filter sessions by status',
      optionAll: 'All',
      optionActive: 'Active',
      optionRevoked: 'Revoked',
      emptyFilteredDescription: 'No session matches the filter.',
      columnDevice: 'Device',
      columnOrg: 'Org',
      columnCreatedAt: 'Created at',
      columnLastUsed: 'Last used',
      columnExpires: 'Expires',
      columnStatus: 'Status',
      statusActive: 'Active',
      statusRevoked: 'Revoked',
      revokeAction: 'Revoke',
    },
    auditTab: {
      scopeDescription:
        'Administrative audit: shows operator actions on this account. Sign-ins and session activity are not part of this trail.',
      viewFullAudit: 'View full audit trail',
      actionLabel: 'Action',
      emptyTitle: 'No actions recorded',
      emptyFilteredDescription: 'No action matches the filter.',
    },
  },
};

const USERS_CONTENT: Bilingual<UsersContent> = {
  'pt-BR': { ...USERS_LIST_CONTENT['pt-BR'], ...USERS_MODALS_CONTENT['pt-BR'] },
  'en-US': { ...USERS_LIST_CONTENT['en-US'], ...USERS_MODALS_CONTENT['en-US'] },
};

export const useUsersContent = (): UsersContent => useBilingual(USERS_CONTENT);

/** Exportado para os defaults pt-BR de `user-error.ts` (chamadores fora de componente não têm
    acesso ao hook). */
export { USERS_CONTENT };
